<template>
  <v-card
    elevation="0"
    outlined
  >
    <v-card-title>
      <div>Traffic Allocation (%) for A/B Test Strategies</div>

      <v-spacer />

      <template v-if="abTestId">
        <v-btn
          class="btn-bg-deep-blue"
          text-color="white"
          @click="openStrategyRoutingTester = true"
        >
          Test Strategy Allocation
        </v-btn>
        <AbTestStrategyRoutingTester
          :is-open="openStrategyRoutingTester"
          :ab-test-id="abTestId"
          @close="openStrategyRoutingTester = false"
        />
      </template>
    </v-card-title>

    <v-card-text v-if="!championStrategy">
      <v-alert
        class="my-10 mx-5"
        outlined
        prominent
        text
        type="warning"
      >
        To configure strategy distributions,
        first select a best lending strategy in the section above. Strategy
        distribution settings will be available once a strategy is selected.
      </v-alert>
    </v-card-text>

    <template v-else>
      <v-card-text>
        <v-overlay
          :value="isLoading"
          absolute
        >
          <v-progress-circular
            indeterminate
            size="64"
          />
        </v-overlay>
        <v-simple-table fixed-header>
          <caption>
            <v-alert
              style="width: 100%;"
              outlined
              class="mt-2 mb-2"
              text
              type="info"
            >
              Any Strategy created here (except the default strategy)
              will be saved as a new lending strategy in the system.
              Please ensure that the strategy name is unique.
            </v-alert>
          </caption>
          <thead>
            <tr>
              <th>Name</th>
              <th>Description</th>
              <th>Traffic %</th>
              <th>Scoring Model</th>
              <th>Product Assignment Models</th>
              <th />
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(validated, index) in $v.trafficAllocations.$each.$iter"
              :key="`allocation-${index}`"
            >
              <td>
                <v-text-field
                  v-model="validated.strategyName.$model"
                  dense
                  style="min-width: 120px"
                  placeholder="Strategy Name"
                  :error-messages="getFieldError('strategyName', index)"
                  :disabled="isReadOnlyStrategy(trafficAllocations[index])"
                />
              </td>

              <td>
                <v-textarea
                  v-model="validated.strategyDescription.$model"
                  style="min-width: 320px"
                  rows="2"
                  placeholder="Strategy Description"
                  auto-grow
                  :error-messages="getFieldError('strategyDescription', index)"
                  :disabled="isReadOnlyStrategy(trafficAllocations[index])"
                />
              </td>

              <td>
                <v-text-field
                  v-model.number="validated.allocationPct.$model"
                  style="width: 84px"
                  dense
                  type="number"
                  min="0"
                  max="100"
                  :error-messages="getFieldError('allocationPct', index)"
                />
              </td>

              <td>
                <v-select
                  v-model="validated.scoringModelId.$model"
                  :disabled="isReadOnlyStrategy(trafficAllocations[index])"
                  :items="scoringModels"
                  placeholder="Scoring Model"
                  item-text="name"
                  item-value="id"
                  required
                  dense
                  :error-messages="getFieldError('scoringModelId', index)"
                />
              </td>

              <td>
                <v-select
                  v-model="validated.productAssignmentModelId.$model"
                  :disabled="isReadOnlyStrategy(trafficAllocations[index])"
                  :items="productAssignmentModels"
                  placeholder="Product Assignment Models"
                  item-text="name"
                  item-value="id"
                  required
                  dense
                  :error-messages="getFieldError('productAssignmentModelId', index)"
                />
              </td>

              <td class="text-right">
                <v-btn
                  v-if="canBeDeleted(index)"
                  color="error"
                  icon
                  small
                  @click="removeStrategy(index)"
                >
                  <v-icon> mdi-trash-can-outline </v-icon>
                </v-btn>
              </td>
            </tr>
          </tbody>
          <tfoot>
            <tr>
              <td
                colspan="6"
                class="text-right"
              >
                <v-btn
                  color="primary"
                  :disabled="isLoading"
                  small
                  @click="addStrategy"
                >
                  <v-icon> mdi-plus </v-icon>
                  Add Strategy
                </v-btn>
              </td>
            </tr>
          </tfoot>
        </v-simple-table>
      </v-card-text>
    </template>
  </v-card>
</template>

<script setup>
import { mapActions } from 'vuex';
import { required } from 'vuelidate/lib/validators';
import AbTestStrategyRoutingTester from './AbTestStrategyRoutingTester.vue';
</script>

<script>
export default {
  name: 'AbTestTrafficAllocations',

  props: {
    value: {
      type: Array,
      default: () => [],
    },
    championStrategy: {
      type: Object,
      default: null,
    },

    abTestId: {
      type: Number,
      default: null,
    },

    serverErrors: {
      type: Object,
      default: () => ({}),
    },
  },

  validations() {
    return {
      trafficAllocations: {
        $each: {
          strategyName: {
            required,
          },
          strategyDescription: {
            required,
          },
          isChampionStrategy: {
            required,
          },
          allocationPct: {
            required,
          },
          scoringModelId: {
            required,
          },
          productAssignmentModelId: {
            required,
          },
        },
      },
    };
  },

  data() {
    return {
      isLoading: false,
      trafficAllocations: [],

      scoringModels: [],
      productAssignmentModels: [],
      validationErrors: {},

      openStrategyRoutingTester: false,
    };
  },

  watch: {
    trafficAllocations: {
      handler(newAllocations) {
        this.$v.$touch();

        this.$emit('valid-traffic-allocations', this.$v.$invalid === false);
        this.$emit('input', newAllocations);
      },
      deep: true,
      immediate: true,
    },

    value: {
      handler(newValue) {
        if (JSON.stringify(newValue) !== JSON.stringify(this.trafficAllocations)) {
          this.trafficAllocations = newValue || [];
          this.setDefaultStrategyDistributions();
        }
      },
      immediate: true,
    },

    championStrategy: {
      immediate: true,
      handler() {
        this.setDefaultStrategyDistributions();
      },
    },
  },

  async created() {
    await this.fetchModels();
  },

  methods: {
    ...mapActions('notification', ['setNotification']),

    async fetchModels() {
      try {
        this.isLoading = true;
        await Promise.all([this.getScoringModels(), this.getProductAssignmentModels()]);
      } catch (error) {
        this.setNotification({
          color: 'red',
          message: 'Failed to fetch scoring models or product assignment models',
        });
      } finally {
        this.isLoading = false;
      }
    },

    async getScoringModels() {
      const { data } = await this.$axios.get('api/v1/scoring-model', {
        params: { perPage: 100 },
      });
      this.scoringModels = data?.data || [];
    },

    async getProductAssignmentModels() {
      const { data } = await this.$axios.get('api/v1/product-assignment-model', {
        params: { perPage: 100 },
      });
      this.productAssignmentModels = data?.data || [];
    },

    isReadOnlyStrategy(strategy) {
      return Boolean(strategy?.isChampionStrategy);
    },

    buildStrategyRow({ isChampionStrategy = false, strategy = null, allocationPct = 0 } = {}) {
      return {
        isChampionStrategy,
        strategyId: strategy?.id ?? null,
        strategyName: strategy ? `${strategy.name}${isChampionStrategy ? ' (Champion)' : ''}` : '',
        strategyDescription: strategy?.description ?? '',
        scoringModelId: strategy?.scoring_mo_id ?? null,
        productAssignmentModelId: strategy?.prod_asgmt_id ?? null,
        allocationPct: Number(allocationPct),
      };
    },

    setDefaultStrategyDistributions() {
      if (!this.championStrategy) {
        return;
      }

      const defaultRow = this.buildStrategyRow({
        isChampionStrategy: true,
        strategy: this.championStrategy,
        allocationPct: 100,
      });

      const existingDefaultIndex = this.trafficAllocations
        ?.findIndex((allocation) => allocation.isChampionStrategy);

      if (existingDefaultIndex !== -1) {
        const existing = this.trafficAllocations[existingDefaultIndex];
        if (existing.strategyId !== this.championStrategy.id) {
          this.trafficAllocations.splice(existingDefaultIndex, 1, {
            ...existing,
            ...defaultRow,
          });
        }
        return;
      }

      this.trafficAllocations = [defaultRow];
    },

    addStrategy() {
      this.trafficAllocations.push(this.buildStrategyRow());
    },

    canBeDeleted(index) {
      // Do not allow deletion of strategies that have been saved.
      return !this.trafficAllocations[index]?.strategyId;
    },

    removeStrategy(index) {
      if (index >= 0 && index < this.trafficAllocations.length) {
        this.trafficAllocations.splice(index, 1);
      }
    },

    getFieldError(fieldName, index) {
      const field = this.$v.trafficAllocations.$each[index][fieldName];

      if (field && field.$dirty && field.$invalid) {
        if (field.$params.required && !field.required) {
          return ['This field is required'];
        }
      }

      const serverErrorKey = `strategy_allocations.${index}.${fieldName}`;

      if (this.serverErrors && this.serverErrors[serverErrorKey]) {
        return this.serverErrors[serverErrorKey];
      }

      return [];
    },
  },
};
</script>
